import React, { useState } from 'react'
import { Play, Trophy, Star, Users, Zap, Gamepad2, Crown, Sword, Shield, Gem, Coins, TrendingUp } from 'lucide-react'

interface GamingMetaverseProps {
  data: {
    gameName: string
    description: string
    genre: string
    platform: string
    totalPlayers: number
    onlinePlayers: number
    tokenSymbol: string
    tokenPrice: string
    tokenPriceChange: number
    features: Array<{
      title: string
      description: string
      icon: string
    }>
    nftAssets: Array<{
      id: string
      name: string
      type: 'character' | 'weapon' | 'land' | 'vehicle' | 'accessory'
      rarity: 'common' | 'rare' | 'epic' | 'legendary'
      price: string
      image: string
      stats?: {
        power: number
        defense: number
        speed: number
      }
    }>
    leaderboard: Array<{
      rank: number
      player: string
      score: number
      level: number
      nftCount: number
      avatar: string
    }>
    quests: Array<{
      id: string
      title: string
      description: string
      reward: string
      difficulty: 'easy' | 'medium' | 'hard'
      timeLimit: string
      participants: number
      completed: boolean
    }>
    marketplace: {
      totalVolume: string
      dailyTrades: number
      averagePrice: string
      topSellers: Array<{
        name: string
        sales: number
        volume: string
      }>
    }
    staking: {
      totalStaked: string
      apy: string
      rewards: Array<{
        token: string
        amount: string
        apy: string
      }>
    }
  }
}

export default function GamingMetaverse({ data }: GamingMetaverseProps) {
  const [selectedTab, setSelectedTab] = useState<'play' | 'marketplace' | 'staking' | 'leaderboard'>('play')
  const [selectedAssetType, setSelectedAssetType] = useState<string>('all')
  const [selectedQuest, setSelectedQuest] = useState<string | null>(null)

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'bg-purple-500'
      case 'epic': return 'bg-blue-500'
      case 'rare': return 'bg-green-500'
      case 'common': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  const getRarityTextColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'text-purple-400'
      case 'epic': return 'text-blue-400'
      case 'rare': return 'text-green-400'
      case 'common': return 'text-gray-400'
      default: return 'text-gray-400'
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-500/20 text-green-400 border-green-400/30'
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-400/30'
      case 'hard': return 'bg-red-500/20 text-red-400 border-red-400/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-400/30'
    }
  }

  const filteredAssets = selectedAssetType === 'all' 
    ? data.nftAssets 
    : data.nftAssets.filter(asset => asset.type === selectedAssetType)

  const activeQuests = data.quests.filter(quest => !quest.completed)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Gamepad2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.gameName}
                </h1>
                <p className="text-purple-200 text-sm">{data.genre} • {data.platform}</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => setSelectedTab('play')}
                className={`transition-colors ${selectedTab === 'play' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Play
              </button>
              <button
                onClick={() => setSelectedTab('marketplace')}
                className={`transition-colors ${selectedTab === 'marketplace' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Marketplace
              </button>
              <button
                onClick={() => setSelectedTab('staking')}
                className={`transition-colors ${selectedTab === 'staking' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Staking
              </button>
              <button
                onClick={() => setSelectedTab('leaderboard')}
                className={`transition-colors ${selectedTab === 'leaderboard' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Leaderboard
              </button>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors">
                Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {data.gameName}
            </h2>
            <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-3xl mx-auto">
              {data.description}
            </p>
            
            {/* Game Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-6 h-6 text-purple-400" />
                  <span className="text-purple-400 font-semibold">Total Players</span>
                </div>
                <div className="text-2xl font-bold text-white">{data.totalPlayers.toLocaleString()}</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-6 h-6 text-green-400" />
                  <span className="text-green-400 font-semibold">Online Now</span>
                </div>
                <div className="text-2xl font-bold text-white">{data.onlinePlayers.toLocaleString()}</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <Coins className="w-6 h-6 text-yellow-400" />
                  <span className="text-yellow-400 font-semibold">{data.tokenSymbol} Price</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-2xl font-bold text-white">{data.tokenPrice}</div>
                  <div className={`text-sm ${data.tokenPriceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {data.tokenPriceChange >= 0 ? '+' : ''}{data.tokenPriceChange}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Game Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {data.features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Gamepad2 className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-purple-200 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Play Tab */}
          {selectedTab === 'play' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h2 
                  className="text-3xl font-bold"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Active Quests
                </h2>
                <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                  <Play className="w-5 h-5 mr-2 inline" />
                  Start New Quest
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeQuests.map((quest) => (
                  <div
                    key={quest.id}
                    className={`bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-300 cursor-pointer ${
                      selectedQuest === quest.id ? 'ring-2 ring-purple-400' : ''
                    }`}
                    onClick={() => setSelectedQuest(selectedQuest === quest.id ? null : quest.id)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getDifficultyColor(quest.difficulty)}`}>
                        {quest.difficulty.charAt(0).toUpperCase() + quest.difficulty.slice(1)}
                      </span>
                      <span className="text-purple-300 text-sm">{quest.participants} players</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3">{quest.title}</h3>
                    <p className="text-purple-200 mb-4">{quest.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-yellow-400">
                        <Gem className="w-4 h-4" />
                        <span className="font-semibold">{quest.reward}</span>
                      </div>
                      <span className="text-purple-300 text-sm">{quest.timeLimit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Marketplace Tab */}
          {selectedTab === 'marketplace' && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <h2 
                  className="text-3xl font-bold"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  NFT Marketplace
                </h2>
                
                <div className="flex gap-4">
                  <select
                    value={selectedAssetType}
                    onChange={(e) => setSelectedAssetType(e.target.value)}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-400"
                  >
                    <option value="all">All Types</option>
                    <option value="character">Characters</option>
                    <option value="weapon">Weapons</option>
                    <option value="land">Land</option>
                    <option value="vehicle">Vehicles</option>
                    <option value="accessory">Accessories</option>
                  </select>
                </div>
              </div>

              {/* Marketplace Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-6 h-6 text-green-400" />
                    <span className="text-green-400 font-semibold">Total Volume</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{data.marketplace.totalVolume}</div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
                  <div className="flex items-center gap-3 mb-2">
                    <Zap className="w-6 h-6 text-blue-400" />
                    <span className="text-blue-400 font-semibold">Daily Trades</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{data.marketplace.dailyTrades}</div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
                  <div className="flex items-center gap-3 mb-2">
                    <Coins className="w-6 h-6 text-purple-400" />
                    <span className="text-purple-400 font-semibold">Average Price</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{data.marketplace.averagePrice}</div>
                </div>
              </div>

              {/* NFT Assets Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-purple-400/50 transition-all duration-300 group"
                  >
                    <div className="aspect-square bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center relative">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                          {asset.type === 'character' && <Users className="w-8 h-8 text-purple-400" />}
                          {asset.type === 'weapon' && <Sword className="w-8 h-8 text-purple-400" />}
                          {asset.type === 'land' && <Shield className="w-8 h-8 text-purple-400" />}
                          {asset.type === 'vehicle' && <Zap className="w-8 h-8 text-purple-400" />}
                          {asset.type === 'accessory' && <Crown className="w-8 h-8 text-purple-400" />}
                        </div>
                        <p className="text-purple-300 text-sm">{asset.name}</p>
                      </div>
                      
                      {/* Rarity Badge */}
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getRarityColor(asset.rarity)}`}>
                          {asset.rarity}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <h3 className="font-semibold text-white mb-2">{asset.name}</h3>
                      <p className="text-purple-300 text-sm mb-3 capitalize">{asset.type}</p>
                      
                      {/* Stats */}
                      {asset.stats && (
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-300">Power</span>
                            <span className="text-white">{asset.stats.power}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-300">Defense</span>
                            <span className="text-white">{asset.stats.defense}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-300">Speed</span>
                            <span className="text-white">{asset.stats.speed}</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-yellow-400">{asset.price}</span>
                        <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staking Tab */}
          {selectedTab === 'staking' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Token Staking
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                  <h3 className="text-2xl font-bold text-white mb-6">Staking Overview</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="text-purple-300">Total Staked</span>
                      <span className="text-2xl font-bold text-white">{data.staking.totalStaked}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-purple-300">Current APY</span>
                      <span className="text-2xl font-bold text-green-400">{data.staking.apy}</span>
                    </div>
                    
                    <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors">
                      Stake Tokens
                    </button>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                  <h3 className="text-2xl font-bold text-white mb-6">Reward Pools</h3>
                  
                  <div className="space-y-4">
                    {data.staking.rewards.map((reward, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div>
                          <div className="font-semibold text-white">{reward.token}</div>
                          <div className="text-purple-300 text-sm">{reward.amount} available</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-400">{reward.apy}</div>
                          <div className="text-purple-300 text-sm">APY</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard Tab */}
          {selectedTab === 'leaderboard' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Top Players
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="space-y-4">
                  {data.leaderboard.map((player) => (
                    <div
                      key={player.rank}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          player.rank <= 3 
                            ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-black' 
                            : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                        }`}>
                          {player.rank <= 3 ? <Trophy className="w-6 h-6" /> : `#${player.rank}`}
                        </div>
                        
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                        
                        <div>
                          <div className="font-semibold text-white">{player.player}</div>
                          <div className="text-purple-300 text-sm">Level {player.level}</div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-bold text-white">{player.score.toLocaleString()}</div>
                        <div className="text-purple-300 text-sm">{player.nftCount} NFTs</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Discord</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">YouTube</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Telegram</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.gameName}. Play, Earn, Own in the Metaverse.
          </p>
        </div>
      </footer>
    </div>
  )
}
